import { Component, OnInit, ChangeDetectionStrategy, OnDestroy} from '@angular/core';
import { ConditionMonitoringService, CONTINUE_TO_MONITOR_RES, IDisplayVehicle, IVisualInspectionBasicApi, MonitoringIssueData } from '../../condition-monitoring.service';
import { map, tap } from 'rxjs/operators';
import { XlatPipe } from '@amt/i18n/xlat-pipe';
import { combineLatest, Observable } from 'rxjs';
import { Inject } from '@angular/core';
import { INextInspectionDateGroup } from '@Models/condition-monitoring/INextInspectionDateGroup';
import { NextInspectionDateGroup } from '@Models/condition-monitoring/NextInspectionDateGroup';
import { IConditonMonitoringTabs } from '@Models/condition-monitoring/IConditionMonitoringTabs';
import { ConditionMonitoringBaseComponent, ICON_HEIGHT, ICON_WIDTH, IGroup } from '../condition-monitoring-base.component';
import { CM_RENDERER_CONFIG, rendererConfigFactory, RendererConfigProvider } from '../renderer-config-provider/cm-renderer-config.provider';

@Component({
    selector: 'condition-monitoring',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './../condition-monitoring-base.component.html',
    styleUrls: ['./condition-monitoring.component.scss'],
    providers: [
        { provide: 'desiredWidth', useValue: ICON_WIDTH },
        { provide: 'desiredHeight', useValue: ICON_HEIGHT },
        { provide: CM_RENDERER_CONFIG, useFactory: rendererConfigFactory },
        ConditionMonitoringService]
})
export class ConditionMonitoringVisualInspectionsComponent extends ConditionMonitoringBaseComponent implements OnInit, OnDestroy {
    _displayBy = [
        { text: this.xlatPipe.transform("conditionmonitoring.action_status"), value: 0 },
        { text: this.xlatPipe.transform("conditionmonitoring.recommended_action"), value: 1 }];
    displayBySelectedItem = this._displayBy.find(elem => elem.value === 1); 

    protected override nextInspectionDateGroups: INextInspectionDateGroup[] = [
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_24h'), undefined, 1),
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_48h'), 1, 2),
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_7d'), 2, 7),
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_14d'), 7, 14),
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_30d'), 14, 30),
        new NextInspectionDateGroup(this.xlatPipe.transform('conditionmonitoring.inspected_non'), 30, undefined),
    ];

    protected override detailsTabs: IConditonMonitoringTabs[] = [
        { 
            title: "conditionmonitoring.tab_conditions_details",
            selected: false,
            type: 'conditionDetails',
        },
        { 
            title: "conditionmonitoring.treadwear",
            selected: false,
            type: 'treadWear',
        },
        {
            title: "conditionmonitoring.visual_inspection_tab",
            selected: true,
            type: 'visualInspection',
        },
    ];

    constructor(
        protected monSvc: ConditionMonitoringService,
        protected xlatPipe: XlatPipe,
        @Inject('WindowFactory') protected WindowFactory: any,
        @Inject('CM_RENDERER_CONFIG') protected rendererConfigProvider: RendererConfigProvider,
    ) {
        super(monSvc, xlatPipe, WindowFactory);
        this.setTitle("conditionmonitoring.VisualInspections");
    }

    public groupedVehicles$: Observable<IGroup<IDisplayVehicle>[]>;

    public onUpdateButtonClick(): void {
        super.toggleFilter(true);

        this.setRendererConfig();
        this.updateData();
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.setRendererConfig();
        this.updateData();
    }

    ngOnDestroy(): void { }
    
    private updateData(): void {
        this.setProcessing();

        this.groupedVehicles$ = combineLatest([this.monSvc.getVehicles$, this.monSvc.visualInspections$]).pipe(
            map(v => this.sortVehicles(v, this.displayBySelectedItem.value)),
            map(v => this.groupBy(v, this.sortBySelectedItem.value)),
            tap(x => this.isProcessing = false)
        );
    }

    /// Used to configure wheel colours and legend
    private setRendererConfig() {
        this.rendererConfig = this.rendererConfigProvider(this.displayBySelectedItem?.value || -1);
    }

    private sortVehicles(data: [IDisplayVehicle[], [string, IVisualInspectionBasicApi[]][]], displayBy: number): IDisplayVehicle[] {
        let vehicles = data[0];
        let issues = data[1];
        let now = new Date();

        for (let issue of issues) {
            let broombroom = vehicles.find((veh) => { return veh.equipmentId === issue[0] })
            const newIssueList: (MonitoringIssueData)[] = [];

            //Per vehicle NOT issue
            let axles = broombroom.spec?.axles;
            let posLabels = [].concat(...axles);

            for (let item of issue[1]) {
                const contMon: boolean = item.action === CONTINUE_TO_MONITOR_RES;
                const nextInspection = this.addDays(new Date(item.inspectionDate), item.inspectionFrequencyDays);
                const inspectionDue = this.addDays(new Date(item.inspectionDate), item.daysToResolve);

                let mi: number;
                switch (displayBy) {
                    case 0: //Action status
                        const isDue: boolean = now >= (item.daysToResolve === 0 ? nextInspection : inspectionDue); 
                        mi = (+(!contMon) << 1) + (+isDue) + 1; //TODO: bad hack
                        break;
                    case 1: //Recommended action
                    default:
                        const daysDue: number = item.daysToResolve;
                        mi = contMon ? 1 : daysDue === 1 ? 4 : daysDue === 7 ? 3 : 2;
                }

                //ensure each position only has the most serious issue - smaller number is less serious issue
                const miDupIdx = newIssueList.findIndex(i => i.posLabel === item.posLabel);
                if (miDupIdx < 0 || mi > newIssueList[miDupIdx].issue || mi === newIssueList[miDupIdx].issue && nextInspection.getTime() < newIssueList[miDupIdx].nextInspection.getTime()) {
                    const newIssue = <MonitoringIssueData>item;
                    newIssue.issue = mi;
                    newIssue.posIdx = posLabels.indexOf(item.posLabel);
                    newIssue.issueText = `Pos ${item.posLabel}: ` + this.xlatPipe.transform(`conditionmonitoring.${this.rendererConfig?.issues[mi]}`);
                    newIssue.nextInspection = nextInspection;
                    newIssue.InspectionDue = inspectionDue;
                    if (miDupIdx < 0) {
                        newIssueList.push(newIssue);
                    } else {
                        newIssueList[miDupIdx] = newIssue;
                    }
                }
            }
            broombroom.issues = newIssueList;
            broombroom.rawIssues = issue[1].map(i => <MonitoringIssueData>i);
            let issueText = newIssueList.sort((a, b) => parseInt(a.posLabel) - parseInt(b.posLabel)).map(m => m.issueText).join("<br />") || this.xlatPipe.transform("conditionmonitoring.NoIssue");//Sort the array here for now. PosLabels were in DESC order (from the database - may have been because of date ordering - leave the sort in anyway?)
            broombroom.altText = this.xlatPipe.transform("conditionmonitoring.VisualInspections") + ":<br />" + issueText;
        };

        if(this.ShowActiveInspectionsOnly)
            return vehicles.filter(x=>x.issues.length>0);

        return vehicles;
    }
}
